import { FC, useEffect, useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { useHistory } from 'react-router-dom'
import { IonButtons, IonTitle } from '@ionic/react'
import { AxiosError } from 'axios'

import { BackButton } from '@components/atoms/BackButton'
import { Title } from '@components/atoms/Title'
import { Loading } from '@/components/atoms/Loading'
import { Main } from '@/components/templates/main'
import {
  CopyWalletAddress,
  ImportantWarning,
  MinimumDepositValue,
} from './components'

import { appConfig } from '@/services/config'
import { useShowErrorToast } from '@/lib/hooks/useShowErrorToast'
import { useDeposit } from '@/lib/hooks/wallet/useDeposit'
import { Routes } from '@/router/routes'

enum DepositErrorMessage {
  USER_NOT_FOUND = 'userNotFound',
  USER_DEACTIVATED = 'userDeactivated',
  USER_NOT_KYC_VERIFIED = 'userNotKycVerified',
  TWO_FACTOR_SETUP_NOT_FOUND = 'twoFactorSetupNotFound',
  GLOBALLY_DEACTIVATED = 'globallyDeactivated',
}

export const Deposit: FC = () => {
  const { t } = useTranslation('common')
  const history = useHistory()
  const {
    data: addressData,
    isLoading: isLoadingAddressData,
    error: addressError,
  } = useDeposit()
  const { showErrorToast } = useShowErrorToast()

  useEffect(() => {
    if (!addressError) return

    if (addressError) {
      const translationKey =
        DepositErrorMessage[(addressError as AxiosError)?.response?.data]
      if (translationKey) {
        showErrorToast({
          message: t(`deposit.errors.${translationKey}` as const),
        })
      }
    }

    setTimeout(() => {
      history.replace('/vault?rpk=true')
    }, 500)
  }, [addressError, history, showErrorToast, t])

  const header = useMemo(
    () => (
      <>
        <IonButtons slot="start">
          <BackButton defaultHref={Routes.PROFILE} />
        </IonButtons>
        <IonTitle className="px-2">
          <Title className="text-center my-0" level={3}>
            {t('deposit.pageTitle', { currency: appConfig.currency })}
          </Title>
        </IonTitle>
      </>
    ),
    [t]
  )

  return (
    <Main
      header={header}
      className="px-8 pb-12"
      headTitle={t('deposit.pageTitle', { currency: appConfig.currency })}
    >
      {isLoadingAddressData || addressError ? (
        <Loading />
      ) : (
        <div>
          <CopyWalletAddress walletAddress={addressData?.walletAddress} />
          <MinimumDepositValue value={addressData?.minDepositAmount} />
          <ImportantWarning />
        </div>
      )}
    </Main>
  )
}
